import { Box, Popover, Typography } from "@mui/material";
import { useState } from "react";

export function TimePicker({
    selectedTime,
    onSelectTime,
    disabled = false,
    error = false,
    helperText = "",
}) {
    // Ref: https://tailgrids.com/components/timepicker

    // Gera opções em formato 24h (padrão Brasil)
    const generateTimeOptions = (interval) => {
        const options = [];
        for (let i = 0; i < 24 * 60; i += interval) {
            const hours = Math.floor(i / 60)
                .toString()
                .padStart(2, "0");
            const minutes = (i % 60).toString().padStart(2, "0");
            options.push(`${hours}:${minutes}`);
        }
        return options;
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const [times] = useState(generateTimeOptions(15)); // Intervalo de 15 min

    const colors = {
        primary: "#3056D3",
        dark: "#090E34",
        stroke: "#E0E0E0",
        body: "#637381",
        white: "#FFFFFF",
        lightBlue: "#F4F7FF",
    };

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const open = Boolean(anchorEl);
    const id = open ? "time-popover" : undefined;

    const handleSelect = (time) => {
        onSelectTime(time);
        handleClose();
    };

    return (
        <Box sx={{ margin: "8px 0 4px 0" }}>
            <Box sx={{ width: "100%" }}>
                <Box sx={{ position: "relative" }}>
                    {/* Ícone de Relógio */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: 14,
                            top: 12,
                            color: colors.body,
                        }}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                        >
                            <path
                                d="M10 18.125C5.5625 18.125 1.9375 14.4688 1.9375 10C1.9375 5.53125 5.5625 1.875 10 1.875C14.4375 1.875 18.0625 5.53125 18.0625 10C18.0625 14.4688 14.4375 18.125 10 18.125ZM10 0.46875C4.78125 0.46875 0.5625 4.75 0.5625 10C0.5625 15.25 4.8125 19.5312 10 19.5312C15.1875 19.5312 19.4375 15.25 19.4375 10C19.4375 4.75 15.2188 0.46875 10 0.46875Z"
                                fill={
                                    disabled
                                        ? "rgba(0, 0, 0, 0.38)"
                                        : "currentColor"
                                }
                            />
                            <path
                                d="M10.4687 10.3125V5.28125C10.4687 4.90625 10.1562 4.59375 9.78125 4.59375C9.40625 4.59375 9.09375 4.90625 9.09375 5.28125V10.5937C9.09375 10.7812 9.15625 10.9687 9.28125 11.0937L12.75 14.625C13.0312 14.9062 13.4687 14.9062 13.75 14.625C14.0312 14.3437 14.0312 13.9062 13.75 13.625L10.4687 10.3125Z"
                                fill={
                                    disabled
                                        ? "rgba(0, 0, 0, 0.38)"
                                        : "currentColor"
                                }
                            />
                        </svg>
                    </Box>

                    {/* Input Disparador */}
                    <input
                        readOnly
                        onClick={handleClick}
                        value={selectedTime}
                        placeholder="00:00"
                        style={{
                            width: "100%",
                            height: "48px",
                            paddingLeft: "48px",
                            borderRadius: "8px",
                            border: `1px solid ${!error ? colors.stroke : "#d32f2f"}`,
                            fontSize: "16px",
                            cursor: disabled ? "default" : "pointer",
                            outline: "none",
                            backgroundColor: colors.white,
                            color: disabled ? "rgba(0, 0, 0, 0.38)" : "inherit",
                        }}
                        disabled={disabled}
                        slotProps={
                            disabled
                                ? {
                                      htmlInput: {
                                          readOnly: true,
                                          disableUnderline: true,
                                      },
                                  }
                                : null
                        }
                    />
                    {helperText && (
                        <Typography
                            sx={{
                                fontSize: "12px",
                                mx: "14px",
                                mt: "3px",
                                color: !error ? "inherit" : "#d32f2f",
                            }}
                        >
                            {helperText}
                        </Typography>
                    )}
                </Box>

                {/* Popover do MUI */}
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        style: {
                            marginTop: "8px",
                            maxHeight: "260px",
                            width: "180px",
                            borderRadius: "8px",
                            boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
                        },
                    }}
                >
                    <Box sx={{ p: 1 }}>
                        {times.map((time) => {
                            const isSelected = selectedTime === time;
                            return (
                                <Box
                                    key={time}
                                    onClick={() => handleSelect(time)}
                                    sx={{
                                        p: "10px",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        borderRadius: "6px",
                                        fontSize: "16px",
                                        fontWeight: isSelected ? 600 : 400,
                                        color: isSelected
                                            ? colors.primary
                                            : colors.dark,
                                        bgcolor: isSelected
                                            ? colors.lightBlue
                                            : "transparent",
                                        "&:hover": {
                                            bgcolor: colors.lightBlue,
                                        },
                                        transition: "0.2s",
                                    }}
                                >
                                    {time}
                                </Box>
                            );
                        })}
                    </Box>
                </Popover>
            </Box>
        </Box>
    );
}
